import {
  Button as AntdButton,
  Descriptions as AntdDescriptions,
  Collapse,
  Divider,
  Dropdown,
  List,
  Menu,
  Rate,
  Space as AntdSpace,
  Table,
  Tag as AntdTag,
} from "antd";
import { MoreOutlined, QuestionCircleTwoTone } from "@ant-design/icons";
import { IQuantity } from "../../../models/product/response";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import AuthContext from "../../../context/auth/context";
import ProductContext from "../../../context/product/context";
import ProductContextProvider from "../../../context/product/provider";
import { ImCheckmark, ImCross } from "react-icons/im";
import Tag from "../../general/antd/tag";
import {
  getProductStatus,
  getProductStatusColor,
  getProductType,
  getProductTypeColor,
} from "../../../models/product/enum";
import ProductAttributes from "../attribute/product-attributes";
import OldImage from "../../form-components/old-image";
import { Permissions } from "../../../utils/Permissions/Permissions";
import Space from "../../general/antd/space";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import Button from "../../general/antd/button";
import Popconfirm from "../../general/antd/pop-confirm";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import AddQuantityButton from "../../general/antd/add-quantity-button";
import MatchQuantityButton from "../../general/antd/match-quantity-button";
import TransferBatchButton from "../../general/antd/transfer-batch-button";
import WriteOffBatchButton from "../../general/antd/write-off-batch-button";
import IdAccountingButton from "../../general/antd/send-id-accounting";
import {
  accountingToByUnit,
  formatQuantityByUnit,
} from "../../../utils/helpers/quantity";

interface Props {}
const desc = ["terrible", "bad", "normal", "good", "wonderful"];
const dropdownMenuItemStyle: React.CSSProperties = { padding: 0 };
const dropdownActionTriggerClassName = "product-actions-dropdown__trigger";

const normalizeBarcodes = (barcodes: unknown): string[] => {
  if (!Array.isArray(barcodes)) {
    return [];
  }

  const seen = new Set<string>();

  return barcodes.reduce<string[]>((result, barcode) => {
    const value = String(barcode).trim();

    if (!value || seen.has(value)) {
      return result;
    }

    seen.add(value);
    result.push(value);
    return result;
  }, []);
};

const Product: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { userPermissions: permissions } = useContext(AuthContext);
  const { details, actions, loading } = useContext(ProductContext);
  const { id } = useParams();
  const navigate = useNavigate();
  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];
  const canManagePurchasePrice = userPermissions.includes(
    Permissions.CAN_MANAGE_PURCHASE_PRICE,
  );
  const renderStoreBarcodes = (barcodes: string[] | undefined) => {
    const values = normalizeBarcodes(barcodes);

    if (values.length === 0) {
      return "-";
    }

    const primaryBarcodeIndex = values.length - 1;

    return (
      <AntdSpace size={[0, 4]} wrap>
        {values.map((barcode, index) => (
          <AntdTag
            key={`${barcode}-${index}`}
            color={index === primaryBarcodeIndex ? "blue" : undefined}
            title={
              index === primaryBarcodeIndex ? t("primary_barcode") : undefined
            }
          >
            {barcode}
          </AntdTag>
        ))}
      </AntdSpace>
    );
  };
  const productActions =
    details && id ? (
      <Space>
        <EditBtn
          onClick={(e) => {
            const url = `/products/update/${id}`;
            if (e.ctrlKey || e.metaKey) {
              window.open(url, "_blank");
            } else {
              navigate(url);
            }
          }}
        />
        <DeleteBtn
          onConfirm={async () => {
            await actions.deleteProduct(details.id);
            eventManager.emit(EVENT_SUCCESS);
            navigate("/products");
          }}
          loading={loading.includes("delete")}
          confirmTitle={
            details?.name
              ? `${t("confirmDelete")} (${t("product_name")}: ${details.name})`
              : undefined
          }
        />
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="circulate" style={dropdownMenuItemStyle}>
                <Popconfirm
                  title={
                    details?.name
                      ? `${t("confirm")} (${t("product_name")}: ${details.name})`
                      : t("confirm")
                  }
                  okButtonProps={{
                    danger: true,
                    loading: loading.includes("update"),
                  }}
                  icon={<QuestionCircleTwoTone twoToneColor="red" />}
                  onConfirm={async () => {
                    await actions.circulateProduct(details.id);
                    await actions.getDetails(details.id);
                    eventManager.emit(EVENT_SUCCESS);
                  }}
                >
                  <span className={dropdownActionTriggerClassName}>
                    {t("circulate_price")}
                  </span>
                </Popconfirm>
              </Menu.Item>

              {userPermissions.includes(
                Permissions.CAN_MATCH_PRODUCT_ACCOUNTING_ID,
              ) && (
                <Menu.Item key="id_accounting" style={dropdownMenuItemStyle}>
                  <IdAccountingButton
                    quantities={details.storeQuantities ?? []}
                    passedId={details.id}
                    productName={details.name}
                    type="text"
                    block
                    className={dropdownActionTriggerClassName}
                  >
                    {t("send_id_accounting")}
                  </IdAccountingButton>
                </Menu.Item>
              )}

              {userPermissions.includes(Permissions.CAN_VIEW_DAILY_STOCK_COUNTS) && userPermissions.includes(Permissions.CAN_CREATE_DAILY_STOCK_COUNTS) && (
                <Menu.Item key="daily_stock_count" style={dropdownMenuItemStyle}>
                  <AntdButton type="text" block className={dropdownActionTriggerClassName} onClick={() => navigate(`/wms/daily-stock-counts?productId=${details.id}`)}>
                    {t("daily_count_record_single")}
                  </AntdButton>
                </Menu.Item>
              )}
              {userPermissions.includes(
                Permissions.CAN_ADD_PRODUCT_QUANTITY,
              ) && (
                <Menu.Item key="add_quantity" style={dropdownMenuItemStyle}>
                  <AddQuantityButton
                    quantities={details.storeQuantities ?? []}
                    passedId={details.id}
                    productName={details.name}
                  rawMaterialId={details.rawMaterialId}
                  soldByUnit={details.soldByUnit}
                    type="text"
                    block
                    className={dropdownActionTriggerClassName}
                  >
                    {t("add_quantity")}
                  </AddQuantityButton>
                </Menu.Item>
              )}

              {userPermissions.includes(
                Permissions.CAN_MATCH_PRODUCT_QUANTITY,
              ) && (
                <Menu.Item key="match_quantity" style={dropdownMenuItemStyle}>
                  <MatchQuantityButton
                  rawMaterialId={details.rawMaterialId}
                    quantities={details.storeQuantities ?? []}
                    passedId={details.id}
                    productName={details.name}
                    type="text"
                    block
                    className={dropdownActionTriggerClassName}
                  >
                    {t("match_quantity")}
                  </MatchQuantityButton>
                </Menu.Item>
              )}

              {userPermissions.includes(Permissions.CAN_TRANSFER_WMS_BATCH) && (
                <Menu.Item key="transfer_batch" style={dropdownMenuItemStyle}>
                  <TransferBatchButton
                    quantities={details.storeQuantities ?? []}
                    passedId={details.id}
                    productName={details.name}
                    type="text"
                    block
                    className={dropdownActionTriggerClassName}
                  >
                    {t("transfer_batch")}
                  </TransferBatchButton>
                </Menu.Item>
              )}

              {userPermissions.includes(
                Permissions.CAN_WRITE_OFF_WMS_BATCH,
              ) && (
                <Menu.Item key="write_off_batch" style={dropdownMenuItemStyle}>
                  <WriteOffBatchButton
                    quantities={details.storeQuantities ?? []}
                    passedId={details.id}
                    productName={details.name}
                    type="text"
                    block
                    className={dropdownActionTriggerClassName}
                  >
                    {t("write_off_batch")}
                  </WriteOffBatchButton>
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
          overlayClassName="product-actions-dropdown"
        >
          <Button shape="circle" icon={<MoreOutlined />} />
        </Dropdown>
      </Space>
    ) : undefined;

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);
  const storeQuantityColumns: any[] = [
    {
      title: t("store"),
      dataIndex: "store_name",
      key: "store_name",
      render: (name: string | { ar: string; en: string }) =>
        typeof name === "object" ? name?.ar : name,
    },
    {
      title: t("scale"),
      dataIndex: "scale_name",
      key: "scale_name",
      render: (name: string | null) => name ?? "-",
    },
    {
      title: t("barcodes"),
      dataIndex: "barcodes",
      key: "barcodes",
      render: (barcodes: string[] | undefined) => renderStoreBarcodes(barcodes),
    },
    {
      title: t("price"),
      dataIndex: "price",
      key: "price",
      render: (price: number) => (price != null ? price.toLocaleString() : "-"),
    },
    {
      // In selling units. A dash means no threshold, which is also why the
      // product never shows up in the purchase forecast for this store.
      title: t("reorder_point"),
      dataIndex: "reorder_point",
      key: "reorder_point",
      render: (value: number | null | undefined) =>
        value != null ? value.toLocaleString() : "-",
    },
    ...(canManagePurchasePrice
      ? [
          {
            title: t("purchase_price"),
            dataIndex: "purchase_price",
            key: "purchase_price",
            render: (price: number) =>
              price != null ? price.toLocaleString() : "-",
          },
        ]
      : []),
    {
      title: t("price_after_discount"),
      dataIndex: "priceAfterDiscount",
      key: "priceAfterDiscount",
      render: (price: number) => (price != null ? price.toLocaleString() : "-"),
    },
    {
      title: t("price_in_dollar"),
      dataIndex: "price_in_dollar",
      key: "price_in_dollar",
      render: (price: number) => (price != null ? `$${price}` : "-"),
    },
    ...(canManagePurchasePrice
      ? [
          {
            title: t("purchase_price_in_dollar"),
            dataIndex: "purchase_price_in_dollar",
            key: "purchase_price_in_dollar",
            render: (price: number) => (price != null ? `$${price}` : "-"),
          },
        ]
      : []),
    {
      title: t("general_discount"),
      dataIndex: "general_discount",
      key: "general_discount",
      render: (val: boolean, record: IQuantity) =>
        val ? `${record.value_general_discount}%` : "-",
    },
    {
      title: t("special_discount"),
      dataIndex: "special_discount",
      key: "special_discount",
      render: (val: boolean, record: IQuantity) =>
        val ? `${record.value_special_discount}%` : "-",
    },
    {
      title: t("quantity"),
      dataIndex: "availableQuantityByUnit",
      key: "quantity",
      render: (_val: number, record: IQuantity) => {
        const factor =
          record.factorAccountingSystem || record.factor_accounting_system;
        const quantityByUnit =
          record.availableQuantityByUnit ??
          (factor ? accountingToByUnit(record.quantity, factor) : undefined);

        return formatQuantityByUnit(
          quantityByUnit,
          record.decimalPlaces,
          factor,
        );
      },
    },
    {
      title: t("available"),
      dataIndex: "available",
      key: "available",
      render: (val: boolean) =>
        val ? <ImCheckmark color="green" /> : <ImCross color="red" />,
    },
    {
      title: t("store_status"),
      dataIndex: "status",
      key: "status",
      render: (val: number) => (
        <Tag
          color={getProductStatusColor(val)}
          title={t(getProductStatus(val))}
        />
      ),
    },
  ];
  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("product")}
        loading={loading.includes("details")}
        onRefresh={() => actions.getDetails(Number(id))}
        headerExtra={productActions}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <>
            <Descriptions size="small" column={1} bordered>
              <AntdDescriptions.Item label={t("id")}>
                {details?.id}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("name")}>
                {details?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("extra_name")}>
                {details?.extraName ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("store")}>
                {details?.store?.name ?? "-"}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("ratesCount")}>
                {details?.ratesCount ?? "-"}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("rate")}>
                <Rate tooltips={desc} disabled defaultValue={details?.rates} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("free_delivery")}>
                {details?.freeDelivery ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("need_recipe")}>
                {details?.needRecipe ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("manufacturing_product_kind")}>
                {t(details?.rawMaterialId ? "manufactured_product" : "raw_product")}
              </AntdDescriptions.Item>
              {details?.rawMaterialId ? <AntdDescriptions.Item label={t("raw_material")}>
                {details.rawMaterial?.name || `#${details.rawMaterialId}`}
              </AntdDescriptions.Item> : null}
              <AntdDescriptions.Item label={t("sold_by_unit")}>
                {details?.soldByUnit ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              {details?.soldByUnit && (
                <AntdDescriptions.Item label={t("scan_quantity_tolerance")}>
                  {details?.scanQuantityTolerance ?? 0}
                </AntdDescriptions.Item>
              )}
              <AntdDescriptions.Item label={t("requires_trip_start_check")}>
                {details?.requiresTripStartCheck ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("note")}>
                {details?.note ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("description")}>
                {details?.description ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: details?.description }}
                  />
                ) : (
                  "-"
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("slice")}>
                {details?.slice?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("main_category")}>
                {details?.mainCategory?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("parent_category")}>
                {details?.parentCategory?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("childern_category")}>
                {details?.childCategory?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("status")}>
                <Tag
                  color={getProductStatusColor(details?.status)}
                  title={t(getProductStatus(details?.status))}
                />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("unit")}>
                <Tag title={details?.unit?.name ?? "-"} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("type")}>
                <Tag
                  color={getProductTypeColor(details?.type)}
                  title={t(getProductType(details?.type))}
                />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("tags")}>
                {details?.tags?.length > 0
                  ? details?.tags?.map((tag) => {
                      return <Tag title={tag?.name} color={tag?.color} />;
                    })
                  : "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("unit_partitions")}>
                {details?.unitPartition?.length > 0
                  ? details?.unitPartition?.map((unitPartition) => {
                      return <Tag title={unitPartition?.name} />;
                    })
                  : "-"}
              </AntdDescriptions.Item>
            </Descriptions>

            <Collapse style={{ marginTop: 16 }}>
              <Collapse.Panel header={t("store_prices")} key="store-prices">
                <Table<IQuantity>
                  dataSource={details?.storeQuantities || []}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={storeQuantityColumns}
                />
              </Collapse.Panel>
            </Collapse>

            <Divider />
            <Descriptions size="small" column={1} bordered>
              <AntdDescriptions.Item label={t("image")}>
                <OldImage src={details?.image?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("file")}>
                <a target="_blank" href={details?.pdf?.url}>
                  download pdf
                </a>
              </AntdDescriptions.Item>
            </Descriptions>

            <Divider />
            <Descriptions column={1} size="small" layout="vertical">
              <AntdDescriptions.Item label={t("attributes")} span={24}>
                <List bordered size="small" style={{ width: "100%" }}>
                  <ProductAttributes attributes={details?.attributes} />
                </List>
              </AntdDescriptions.Item>
            </Descriptions>
          </>
        )}
      </PageDetails>
    </>
  );
};

const ProductDetailesPage = () => {
  return (
    <ProductContextProvider>
      <Product />
    </ProductContextProvider>
  );
};
export default ProductDetailesPage;
